import React, { useId, useMemo } from 'react';
import { Goal } from '../../services/goalService';
import { useTranslation } from '../../i18n';
import { dayKey, daysBetween, todayKey } from '../../utils/zonedTime';
import './MotivationBanner.css';

type MotivationState = 'progress' | 'static' | 'regression';

function getMotivationState(goals: Goal[]): MotivationState {
  const allTodos = goals.flatMap(g =>
    g.subGoals.flatMap(s => s.todos)
  );
  const completedWithDate = allTodos.filter(
    t => t.completed && t.completedAt
  );

  if (completedWithDate.length === 0) return 'static';

  const completionDates = completedWithDate
    .map(t => new Date(t.completedAt!))
    .filter(date => !Number.isNaN(date.getTime()));

  if (completionDates.length === 0) return 'static';

  const mostRecentMs = Math.max(
    ...completionDates.map(date => date.getTime())
  );
  // Calendar days in the account's time zone
  const daysSince = daysBetween(dayKey(mostRecentMs), todayKey());

  if (daysSince <= 0) return 'progress';
  if (daysSince <= 2) return 'static';
  return 'regression';
}

// ─── PROGRESS SVG ────────────────────────────────────────────────────────────
function ProgressSVG({ p }: { p: string }) {
  return (
    <svg viewBox="0 0 340 280" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id={`${p}sg1`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(56, 189, 248, 0.25)"/>
          <stop offset="100%" stopColor="rgba(56, 189, 248, 0.03)"/>
        </linearGradient>
        <linearGradient id={`${p}sg2`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(99, 102, 241, 0.25)"/>
          <stop offset="100%" stopColor="rgba(99, 102, 241, 0.03)"/>
        </linearGradient>
        <linearGradient id={`${p}edgeG`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#38bdf8"/>
          <stop offset="50%" stopColor="#818cf8"/>
          <stop offset="100%" stopColor="#c084fc"/>
        </linearGradient>
        <linearGradient id={`${p}trailG`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0"/>
          <stop offset="100%" stopColor="#c084fc" stopOpacity="0.8"/>
        </linearGradient>
        <radialGradient id={`${p}starGlow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fde047" stopOpacity="0.8"/>
          <stop offset="60%" stopColor="#fde047" stopOpacity="0.15"/>
          <stop offset="100%" stopColor="#fde047" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id={`${p}starCore`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fef9c3"/>
          <stop offset="100%" stopColor="#fde047"/>
        </radialGradient>
        <linearGradient id={`${p}shirtG`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff6666"/>
          <stop offset="100%" stopColor="#ee4444"/>
        </linearGradient>
        <linearGradient id={`${p}skinG`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffaa77"/>
          <stop offset="100%" stopColor="#ff9966"/>
        </linearGradient>
        <filter id={`${p}gl`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="4" result="b"/>
          <feComposite in="SourceGraphic" in2="b" operator="over"/>
        </filter>
        <filter id={`${p}pShadow`}>
          <feDropShadow dx="1" dy="3" stdDeviation="2.5" floodColor="rgba(0,0,0,0.22)"/>
        </filter>
        <filter id={`${p}stepGlow`} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="8"/>
        </filter>
        <style>{`
          .${p}person-container { animation: ${p}personFade 12s ease-in-out infinite; }
          @keyframes ${p}personFade {
            0%   { opacity: 0; } 4% { opacity: 1; }
            75%  { opacity: 1; } 84% { opacity: 0; } 100% { opacity: 0; }
          }
          .${p}w-leg-l { animation: ${p}wLegL 0.5s cubic-bezier(.35,.05,.55,.95) infinite alternate; }
          .${p}w-leg-r { animation: ${p}wLegR 0.5s cubic-bezier(.35,.05,.55,.95) infinite alternate; }
          @keyframes ${p}wLegL { 0% { transform: rotate(-22deg); } 100% { transform: rotate(22deg); } }
          @keyframes ${p}wLegR { 0% { transform: rotate(22deg); }  100% { transform: rotate(-22deg); } }
          .${p}w-arm-l { animation: ${p}wArmL 0.5s cubic-bezier(.35,.05,.55,.95) infinite alternate; }
          .${p}w-arm-r { animation: ${p}wArmR 0.5s cubic-bezier(.35,.05,.55,.95) infinite alternate; }
          @keyframes ${p}wArmL { 0% { transform: rotate(28deg); }  100% { transform: rotate(-22deg); } }
          @keyframes ${p}wArmR { 0% { transform: rotate(-22deg); } 100% { transform: rotate(28deg); } }
          .${p}w-body { animation: ${p}wBounce 0.25s ease-in-out infinite alternate; }
          @keyframes ${p}wBounce { 0% { transform: translateY(0) rotate(0deg); } 100% { transform: translateY(-1.5px) rotate(-1.5deg); } }
          .${p}pose-walk    { animation: ${p}pWalk 12s infinite; }
          .${p}pose-victory { animation: ${p}pVictory 12s infinite; }
          @keyframes ${p}pWalk    { 0%,60%{opacity:1} 65%,78%{opacity:0} 84%,100%{opacity:1} }
          @keyframes ${p}pVictory { 0%,60%{opacity:0} 65%{opacity:1} 78%{opacity:1} 84%,100%{opacity:0} }
          .${p}victory-bounce { animation: ${p}vBounce 12s infinite; }
          @keyframes ${p}vBounce {
            0%,60%{transform:translateY(0) scale(1)} 65%{transform:translateY(-4px) scale(1.03)}
            68%{transform:translateY(0) scale(1)} 70%{transform:translateY(-2px) scale(1.01)} 73%,100%{transform:translateY(0) scale(1)}
          }
          .${p}star-anim { animation: ${p}starReveal 12s infinite; }
          @keyframes ${p}starReveal {
            0%,56%{opacity:0;transform:scale(0.3) rotate(-20deg)} 64%{opacity:1;transform:scale(1.2) rotate(10deg)}
            68%{opacity:1;transform:scale(0.95) rotate(-3deg)} 72%,76%{opacity:1;transform:scale(1) rotate(0deg)} 82%,100%{opacity:0;transform:scale(0.5) rotate(-10deg)}
          }
          .${p}star-pulse { animation: ${p}starPulse 2s ease-in-out infinite; }
          @keyframes ${p}starPulse { 0%,100%{opacity:0.6;transform:scale(1)} 50%{opacity:1;transform:scale(1.15)} }
          .${p}star-rays  { animation: ${p}starRays 3s linear infinite; }
          @keyframes ${p}starRays { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
          .${p}confetti { animation: ${p}confettiFall 12s infinite; }
          @keyframes ${p}confettiFall {
            0%,62%{opacity:0;transform:translateY(0) rotate(0deg) scale(0)} 66%{opacity:1;transform:translateY(-14px) rotate(120deg) scale(1.2)}
            74%{opacity:0.9;transform:translateY(8px) rotate(240deg) scale(1)} 80%{opacity:0.4;transform:translateY(30px) rotate(340deg) scale(0.6)} 84%,100%{opacity:0;transform:translateY(45px) rotate(400deg) scale(0)}
          }
          .${p}confetti:nth-child(2){animation-delay:0.1s} .${p}confetti:nth-child(3){animation-delay:0.25s}
          .${p}confetti:nth-child(4){animation-delay:0.08s} .${p}confetti:nth-child(5){animation-delay:0.2s}
          .${p}confetti:nth-child(6){animation-delay:0.04s} .${p}confetti:nth-child(7){animation-delay:0.18s}
          .${p}confetti:nth-child(8){animation-delay:0.12s}
          .${p}stair-glow-1{animation:${p}sGlow1 12s infinite} .${p}stair-glow-2{animation:${p}sGlow2 12s infinite}
          .${p}stair-glow-3{animation:${p}sGlow3 12s infinite} .${p}stair-glow-4{animation:${p}sGlow4 12s infinite}
          .${p}stair-glow-5{animation:${p}sGlow5 12s infinite}
          @keyframes ${p}sGlow1{0%,4%{opacity:0}8%{opacity:0.7}14%{opacity:0.25}80%,100%{opacity:0}}
          @keyframes ${p}sGlow2{0%,14%{opacity:0}20%{opacity:0.7}28%{opacity:0.25}80%,100%{opacity:0}}
          @keyframes ${p}sGlow3{0%,28%{opacity:0}34%{opacity:0.7}42%{opacity:0.25}80%,100%{opacity:0}}
          @keyframes ${p}sGlow4{0%,40%{opacity:0}46%{opacity:0.7}54%{opacity:0.25}80%,100%{opacity:0}}
          @keyframes ${p}sGlow5{0%,52%{opacity:0}58%{opacity:0.8}66%{opacity:0.3}80%,100%{opacity:0}}
          .${p}trail-line{animation:${p}trailReveal 12s infinite;stroke-dasharray:300;stroke-dashoffset:300}
          @keyframes ${p}trailReveal{0%{stroke-dashoffset:300;opacity:0}5%{opacity:0.8}60%{stroke-dashoffset:0;opacity:0.8}70%{stroke-dashoffset:0;opacity:0.6}84%,100%{stroke-dashoffset:0;opacity:0}}
          .${p}ripple{animation:${p}ripple 12s infinite;opacity:0}
          @keyframes ${p}ripple{0%,7%{r:0;opacity:0}9%{r:8;opacity:0.5}14%{r:16;opacity:0}100%{opacity:0}}
          .${p}ripple-2{animation:${p}ripple2 12s infinite;opacity:0}
          @keyframes ${p}ripple2{0%,19%{r:0;opacity:0}22%{r:8;opacity:0.5}28%{r:16;opacity:0}100%{opacity:0}}
          .${p}ripple-3{animation:${p}ripple3 12s infinite;opacity:0}
          @keyframes ${p}ripple3{0%,33%{r:0;opacity:0}36%{r:8;opacity:0.5}42%{r:16;opacity:0}100%{opacity:0}}
          .${p}ripple-4{animation:${p}ripple4 12s infinite;opacity:0}
          @keyframes ${p}ripple4{0%,46%{r:0;opacity:0}49%{r:8;opacity:0.5}55%{r:16;opacity:0}100%{opacity:0}}
          .${p}ripple-5{animation:${p}ripple5 12s infinite;opacity:0}
          @keyframes ${p}ripple5{0%,56%{r:0;opacity:0}59%{r:8;opacity:0.5}65%{r:16;opacity:0}100%{opacity:0}}
          .${p}amb{animation:${p}ambFloat 6s infinite ease-in-out}
          .${p}amb:nth-child(2){animation-delay:1.2s;animation-duration:7s}.${p}amb:nth-child(3){animation-delay:2.8s;animation-duration:5s}
          .${p}amb:nth-child(4){animation-delay:0.6s;animation-duration:8s}.${p}amb:nth-child(5){animation-delay:1.8s;animation-duration:6.5s}
          .${p}amb:nth-child(6){animation-delay:3.5s;animation-duration:5.5s}.${p}amb:nth-child(7){animation-delay:0.3s;animation-duration:9s}
          @keyframes ${p}ambFloat{0%{transform:translateY(0) scale(1);opacity:0}20%{opacity:0.5}50%{transform:translateY(-18px) scale(1.3);opacity:0.35}80%{opacity:0.15}100%{transform:translateY(-35px) scale(0.8);opacity:0}}
          .${p}geo{animation:${p}geoFloat 8s ease-in-out infinite}
          @keyframes ${p}geoFloat{0%,100%{transform:translateY(0) rotate(0deg);opacity:0.12}50%{transform:translateY(-8px) rotate(180deg);opacity:0.25}}
        `}</style>
      </defs>

      <circle className={`${p}amb`} cx="55" cy="190" r="1.8" fill="#38bdf8"/>
      <circle className={`${p}amb`} cx="170" cy="120" r="2" fill="#fde047"/>
      <circle className={`${p}amb`} cx="35" cy="90" r="1.3" fill="#a78bfa"/>
      <circle className={`${p}amb`} cx="285" cy="155" r="1.8" fill="#34d399"/>
      <circle className={`${p}amb`} cx="305" cy="98" r="1.2" fill="#f472b6"/>
      <circle className={`${p}amb`} cx="130" cy="70" r="1.5" fill="#38bdf8"/>
      <circle className={`${p}amb`} cx="260" cy="200" r="1" fill="#818cf8"/>
      <rect className={`${p}geo`} x="20" y="120" width="5" height="5" rx="1" fill="#38bdf8" opacity="0.15" style={{transformOrigin:'22px 122px'}}/>
      <polygon className={`${p}geo`} points="310,130 313,124 316,130" fill="#c084fc" opacity="0.12" style={{transformOrigin:'313px 127px',animationDelay:'2s'}}/>
      <circle className={`${p}geo`} cx="290" cy="60" r="2.5" fill="#fde047" opacity="0.1" style={{animationDelay:'4s'}}/>

      <g>
        <rect x="40" y="205" width="42" height="55" rx="7" fill={`url(#${p}sg1)`} opacity="0.65"/>
        <rect x="82" y="175" width="42" height="85" rx="7" fill={`url(#${p}sg1)`} opacity="0.70"/>
        <rect x="124" y="145" width="42" height="115" rx="7" fill={`url(#${p}sg2)`} opacity="0.65"/>
        <rect x="166" y="115" width="42" height="145" rx="7" fill={`url(#${p}sg2)`} opacity="0.70"/>
        <rect x="208" y="85" width="52" height="175" rx="7" fill={`url(#${p}sg2)`} opacity="0.75"/>
        <path d="M40,205 L82,205 L82,175 L124,175 L124,145 L166,145 L166,115 L208,115 L208,85 L260,85" fill="none" stroke={`url(#${p}edgeG)`} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.5"/>
        <path d="M40,205 L82,205 L82,175 L124,175 L124,145 L166,145 L166,115 L208,115 L208,85 L260,85" fill="none" stroke={`url(#${p}edgeG)`} strokeWidth="6" strokeLinejoin="round" strokeLinecap="round" opacity="0.08" filter={`url(#${p}gl)`}/>
        <path className={`${p}trail-line`} d="M40,205 L82,205 L82,175 L124,175 L124,145 L166,145 L166,115 L208,115 L208,85 L260,85" fill="none" stroke={`url(#${p}edgeG)`} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
        <circle className={`${p}stair-glow-1`} cx="61" cy="205" r="18" fill="#38bdf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-2`} cx="103" cy="175" r="18" fill="#38bdf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-3`} cx="145" cy="145" r="18" fill="#818cf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-4`} cx="187" cy="115" r="18" fill="#818cf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-5`} cx="234" cy="85" r="20" fill="#c084fc" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}ripple`} cx="61" cy="205" r="0" fill="none" stroke="#38bdf8" strokeWidth="1"/>
        <circle className={`${p}ripple-2`} cx="103" cy="175" r="0" fill="none" stroke="#38bdf8" strokeWidth="1"/>
        <circle className={`${p}ripple-3`} cx="145" cy="145" r="0" fill="none" stroke="#818cf8" strokeWidth="1"/>
        <circle className={`${p}ripple-4`} cx="187" cy="115" r="0" fill="none" stroke="#818cf8" strokeWidth="1"/>
        <circle className={`${p}ripple-5`} cx="234" cy="85" r="0" fill="none" stroke="#c084fc" strokeWidth="1"/>
      </g>

      <g className={`${p}star-anim`} style={{transformOrigin:'234px 56px'}}>
        <circle cx="234" cy="56" r="32" fill={`url(#${p}starGlow)`} className={`${p}star-pulse`} style={{transformOrigin:'234px 56px'}}/>
        <g className={`${p}star-rays`} style={{transformOrigin:'234px 56px'}} opacity="0.3">
          <line x1="234" y1="30" x2="234" y2="24" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="234" y1="82" x2="234" y2="88" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="208" y1="56" x2="202" y2="56" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="260" y1="56" x2="266" y2="56" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="216" y1="38" x2="212" y2="34" stroke="#fde047" strokeWidth="1" strokeLinecap="round"/>
          <line x1="252" y1="74" x2="256" y2="78" stroke="#fde047" strokeWidth="1" strokeLinecap="round"/>
          <line x1="216" y1="74" x2="212" y2="78" stroke="#fde047" strokeWidth="1" strokeLinecap="round"/>
          <line x1="252" y1="38" x2="256" y2="34" stroke="#fde047" strokeWidth="1" strokeLinecap="round"/>
        </g>
        <polygon points="234,36 238.5,50 254,50 241.5,60 246,75 234,65 222,75 226.5,60 214,50 229.5,50" fill={`url(#${p}starCore)`} opacity="0.95"/>
        <polygon points="234,42 237,52 247,52 239,58 242,69 234,62 226,69 229,58 221,52 231,52" fill="#fef9c3" opacity="0.9"/>
      </g>

      <g>
        <rect className={`${p}confetti`} x="218" y="62" width="4" height="4" rx="1" fill="#38bdf8" style={{transformOrigin:'220px 64px'}}/>
        <rect className={`${p}confetti`} x="242" y="58" width="3" height="5" rx="1" fill="#f472b6" style={{transformOrigin:'244px 61px'}}/>
        <circle className={`${p}confetti`} cx="230" cy="54" r="2.5" fill="#fde047" style={{transformOrigin:'230px 54px'}}/>
        <rect className={`${p}confetti`} x="250" y="66" width="4" height="3" rx="1" fill="#34d399" style={{transformOrigin:'252px 68px'}}/>
        <rect className={`${p}confetti`} x="213" y="56" width="3" height="4" rx="1" fill="#c084fc" style={{transformOrigin:'215px 58px'}}/>
        <circle className={`${p}confetti`} cx="253" cy="53" r="2" fill="#fb923c" style={{transformOrigin:'253px 53px'}}/>
        <rect className={`${p}confetti`} x="224" y="48" width="3" height="3" rx="0.5" fill="#38bdf8" style={{transformOrigin:'226px 50px'}}/>
        <rect className={`${p}confetti`} x="248" y="72" width="3.5" height="3.5" rx="1" fill="#fde047" style={{transformOrigin:'250px 74px'}}/>
      </g>

      <g className={`${p}person-container`} filter={`url(#${p}pShadow)`}>
        <g transform="translate(61, 158)">
          <g>
            <animateTransform attributeName="transform" type="translate"
              values="0,0; 0,0; 42,-30; 42,-30; 84,-60; 84,-60; 126,-90; 126,-90; 173,-120; 173,-120; 173,-120; 173,-120; 0,0"
              keyTimes="0; 0.06; 0.16; 0.24; 0.32; 0.40; 0.46; 0.52; 0.58; 0.62; 0.80; 0.86; 1"
              keySplines="0.25 0.1 0.25 1; 0.42 0 0.58 1; 0.25 0.1 0.25 1; 0.42 0 0.58 1; 0.25 0.1 0.25 1; 0.42 0 0.58 1; 0.25 0.1 0.25 1; 0.42 0 0.58 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.42 0 0.58 1"
              calcMode="spline" dur="12s" repeatCount="indefinite"/>

            <g className={`${p}pose-walk`}>
              <g className={`${p}w-body`}>
                <ellipse cx="0" cy="47" rx="13" ry="3" fill="rgba(0,0,0,0.15)">
                  <animate attributeName="rx" values="13;11;13" dur="0.5s" repeatCount="indefinite"/>
                </ellipse>
                <g className={`${p}w-leg-l`} style={{transformOrigin:'-1px 22px'}}>
                  <path d="M-1,22 L-6,34 L-7,41" stroke="#444" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
                  <path d="M-7,41 L-12,43.5 L-4,44.5 L-6,41" fill="#552200" stroke="none"/>
                </g>
                <g className={`${p}w-leg-r`} style={{transformOrigin:'2px 22px'}}>
                  <path d="M2,22 L8,34 L9,41" stroke="#333" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
                  <path d="M9,41 L4,43.5 L12,44.5 L10,41" fill="#441a00" stroke="none"/>
                </g>
                <path d="M-9,4 C-9,0 -5,-3 0,-3 C5,-3 9,0 9,4 L8,23 L-8,23 Z" fill={`url(#${p}shirtG)`}/>
                <path d="M-4,-1 Q0,-4 4,-1" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
                <path d="M-6,4 Q-4,12 -5,20" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <g className={`${p}w-arm-l`} style={{transformOrigin:'-7px 2px'}}>
                  <path d="M-7,2 L-14,13 L-16,18" stroke={`url(#${p}skinG)`} strokeWidth="4.2" strokeLinecap="round" fill="none"/>
                  <circle cx="-16.5" cy="19" r="2.3" fill="#ff9966"/>
                </g>
                <g className={`${p}w-arm-r`} style={{transformOrigin:'7px 2px'}}>
                  <path d="M7,2 L14,13 L16,18" stroke="#e88855" strokeWidth="4.2" strokeLinecap="round" fill="none"/>
                  <circle cx="16.5" cy="19" r="2.3" fill="#e88855"/>
                </g>
                <rect x="-2.8" y="-8" width="5.6" height="6" rx="2.8" fill={`url(#${p}skinG)`}/>
                <circle cx="0" cy="-16" r="11" fill={`url(#${p}skinG)`}/>
                <path d="M-11,-21 Q-10,-30 0,-32 Q10,-30 11,-21 L11,-17 Q9,-23 0,-25 Q-9,-23 -11,-17 Z" fill="#1a1a1a"/>
                <path d="M-11,-21 Q-11.5,-17 -10,-14" stroke="#1a1a1a" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
                <path d="M9,-23 Q11.5,-20 11,-17" stroke="#1a1a1a" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
                <path d="M-4,-30 Q0,-31 3,-29" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <ellipse cx="-10.5" cy="-14.5" rx="2.2" ry="2.8" fill="#e88855"/>
                <ellipse cx="-10.5" cy="-14.5" rx="1.2" ry="1.5" fill="#dd7744" opacity="0.4"/>
                <circle cx="3.5" cy="-17" r="1.8" fill="#1a1a1a"/>
                <circle cx="4.2" cy="-17.5" r="0.7" fill="white"/>
                <path d="M1,-20 Q3.5,-21.2 6.5,-20" stroke="#1a1a1a" strokeWidth="1.1" fill="none" strokeLinecap="round"/>
                <path d="M5,-14 Q5.5,-13 5,-12.5" stroke="#dd8866" strokeWidth="0.7" fill="none" strokeLinecap="round"/>
                <path d="M1.5,-11.5 Q4,-9 7,-11.5" stroke="#cc7744" strokeWidth="0.9" fill="none" strokeLinecap="round"/>
                <ellipse cx="7" cy="-12.5" rx="2.5" ry="1.2" fill="#ff8888" opacity="0.25"/>
              </g>
            </g>

            <g className={`${p}pose-victory`}>
              <g className={`${p}victory-bounce`}>
                <ellipse cx="0" cy="47" rx="10" ry="2.5" fill="rgba(0,0,0,0.12)"/>
                <path d="M-4,22 L-5,37 L-6,42" stroke="#444" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
                <path d="M-6,42 L-11,44 L-3,45 L-5,42" fill="#552200" stroke="none"/>
                <path d="M4,22 L5,37 L6,42" stroke="#333" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
                <path d="M6,42 L1,44 L9,45 L7,42" fill="#441a00" stroke="none"/>
                <path d="M-9,4 C-9,0 -5,-3 0,-3 C5,-3 9,0 9,4 L8,23 L-8,23 Z" fill={`url(#${p}shirtG)`}/>
                <path d="M-4,-1 Q0,-4 4,-1" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
                <path d="M-6,4 Q-4,12 -5,20" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <path d="M-7,2 L-17,-12 L-21,-22" stroke={`url(#${p}skinG)`} strokeWidth="4.2" strokeLinecap="round" fill="none"/>
                <circle cx="-21.5" cy="-23" r="2.3" fill="#ff9966"/>
                <path d="M7,2 L17,-12 L21,-22" stroke="#e88855" strokeWidth="4.2" strokeLinecap="round" fill="none"/>
                <circle cx="21.5" cy="-23" r="2.3" fill="#e88855"/>
                <rect x="-2.8" y="-8" width="5.6" height="6" rx="2.8" fill={`url(#${p}skinG)`}/>
                <circle cx="0" cy="-17" r="11" fill={`url(#${p}skinG)`}/>
                <path d="M-11,-22 Q-10,-31 0,-33 Q10,-31 11,-22 L11,-18 Q9,-24 0,-26 Q-9,-24 -11,-18 Z" fill="#1a1a1a"/>
                <path d="M-11,-22 Q-11.5,-18 -10,-15" stroke="#1a1a1a" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
                <path d="M-4,-31 Q0,-32 3,-30" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <ellipse cx="-10.5" cy="-16" rx="2.2" ry="2.8" fill="#e88855"/>
                <path d="M1,-19 Q3.5,-21.5 6,-19" stroke="#1a1a1a" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
                <path d="M-6,-19 Q-3.5,-21.5 -1,-19" stroke="#1a1a1a" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
                <path d="M-4,-12 Q0,-7.5 5,-12" stroke="#cc7744" strokeWidth="1.1" fill="none" strokeLinecap="round"/>
                <ellipse cx="7" cy="-14" rx="2.8" ry="1.3" fill="#ff8888" opacity="0.35"/>
                <ellipse cx="-6" cy="-14" rx="2.8" ry="1.3" fill="#ff8888" opacity="0.35"/>
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}

// ─── STATIC SVG ──────────────────────────────────────────────────────────────
function StaticSVG({ p }: { p: string }) {
  return (
    <svg viewBox="0 0 340 280" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id={`${p}sg1`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(56, 189, 248, 0.25)"/>
          <stop offset="100%" stopColor="rgba(56, 189, 248, 0.03)"/>
        </linearGradient>
        <linearGradient id={`${p}sg2`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(99, 102, 241, 0.25)"/>
          <stop offset="100%" stopColor="rgba(99, 102, 241, 0.03)"/>
        </linearGradient>
        <linearGradient id={`${p}edgeG`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#38bdf8"/>
          <stop offset="50%" stopColor="#818cf8"/>
          <stop offset="100%" stopColor="#c084fc"/>
        </linearGradient>
        <radialGradient id={`${p}starGlow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fde047" stopOpacity="0.8"/>
          <stop offset="60%" stopColor="#fde047" stopOpacity="0.15"/>
          <stop offset="100%" stopColor="#fde047" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id={`${p}starCore`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fef9c3"/>
          <stop offset="100%" stopColor="#fde047"/>
        </radialGradient>
        <linearGradient id={`${p}shirtG`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff6666"/>
          <stop offset="100%" stopColor="#ee4444"/>
        </linearGradient>
        <linearGradient id={`${p}skinG`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffaa77"/>
          <stop offset="100%" stopColor="#ff9966"/>
        </linearGradient>
        <filter id={`${p}gl`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="4" result="b"/>
          <feComposite in="SourceGraphic" in2="b" operator="over"/>
        </filter>
        <filter id={`${p}pShadow`}>
          <feDropShadow dx="1" dy="3" stdDeviation="2.5" floodColor="rgba(0,0,0,0.22)"/>
        </filter>
        <filter id={`${p}stepGlow`} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="8"/>
        </filter>
        <style>{`
          .${p}person-container { opacity: 1; }
          .${p}sit-body { animation: ${p}sitBreathe 3s ease-in-out infinite; }
          @keyframes ${p}sitBreathe { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-0.8px)} }
          .${p}thought-bubble { animation: ${p}thoughtLoop 4s ease-in-out infinite; }
          @keyframes ${p}thoughtLoop { 0%,100%{opacity:1;transform:translateY(0) scale(1)} 50%{opacity:1;transform:translateY(-2px) scale(1.05)} }
          .${p}bulb-glow { animation: ${p}bulbPulse 2.5s ease-in-out infinite; }
          @keyframes ${p}bulbPulse { 0%,100%{opacity:0.7} 50%{opacity:1} }
          .${p}star-anim { opacity: 1; }
          .${p}star-pulse { animation: ${p}starPulse 2s ease-in-out infinite; }
          @keyframes ${p}starPulse { 0%,100%{opacity:0.6;transform:scale(1)} 50%{opacity:1;transform:scale(1.15)} }
          .${p}star-rays { animation: ${p}starRays 3s linear infinite; }
          @keyframes ${p}starRays { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
          .${p}stair-glow-1,.${p}stair-glow-2,.${p}stair-glow-4,.${p}stair-glow-5 { opacity: 0; }
          .${p}stair-glow-3 { animation: ${p}sGlow3 3s ease-in-out infinite; }
          @keyframes ${p}sGlow3 { 0%,100%{opacity:0.25} 50%{opacity:0.55} }
          .${p}trail-line { opacity: 0; }
          .${p}amb { animation: ${p}ambFloat 6s infinite ease-in-out; }
          .${p}amb:nth-child(2){animation-delay:1.2s;animation-duration:7s}.${p}amb:nth-child(3){animation-delay:2.8s;animation-duration:5s}
          .${p}amb:nth-child(4){animation-delay:0.6s;animation-duration:8s}.${p}amb:nth-child(5){animation-delay:1.8s;animation-duration:6.5s}
          .${p}amb:nth-child(6){animation-delay:3.5s;animation-duration:5.5s}.${p}amb:nth-child(7){animation-delay:0.3s;animation-duration:9s}
          @keyframes ${p}ambFloat{0%{transform:translateY(0) scale(1);opacity:0}20%{opacity:0.5}50%{transform:translateY(-18px) scale(1.3);opacity:0.35}80%{opacity:0.15}100%{transform:translateY(-35px) scale(0.8);opacity:0}}
          .${p}geo{animation:${p}geoFloat 8s ease-in-out infinite}
          @keyframes ${p}geoFloat{0%,100%{transform:translateY(0) rotate(0deg);opacity:0.12}50%{transform:translateY(-8px) rotate(180deg);opacity:0.25}}
        `}</style>
      </defs>

      <circle className={`${p}amb`} cx="55" cy="190" r="1.8" fill="#38bdf8"/>
      <circle className={`${p}amb`} cx="170" cy="120" r="2" fill="#fde047"/>
      <circle className={`${p}amb`} cx="35" cy="90" r="1.3" fill="#a78bfa"/>
      <circle className={`${p}amb`} cx="285" cy="155" r="1.8" fill="#34d399"/>
      <circle className={`${p}amb`} cx="305" cy="98" r="1.2" fill="#f472b6"/>
      <circle className={`${p}amb`} cx="130" cy="70" r="1.5" fill="#38bdf8"/>
      <circle className={`${p}amb`} cx="260" cy="200" r="1" fill="#818cf8"/>
      <rect className={`${p}geo`} x="20" y="120" width="5" height="5" rx="1" fill="#38bdf8" opacity="0.15" style={{transformOrigin:'22px 122px'}}/>
      <polygon className={`${p}geo`} points="310,130 313,124 316,130" fill="#c084fc" opacity="0.12" style={{transformOrigin:'313px 127px',animationDelay:'2s'}}/>
      <circle className={`${p}geo`} cx="290" cy="60" r="2.5" fill="#fde047" opacity="0.1" style={{animationDelay:'4s'}}/>

      <g>
        <rect x="40" y="205" width="42" height="55" rx="7" fill={`url(#${p}sg1)`} opacity="0.65"/>
        <rect x="82" y="175" width="42" height="85" rx="7" fill={`url(#${p}sg1)`} opacity="0.70"/>
        <rect x="124" y="145" width="42" height="115" rx="7" fill={`url(#${p}sg2)`} opacity="0.65"/>
        <rect x="166" y="115" width="42" height="145" rx="7" fill={`url(#${p}sg2)`} opacity="0.70"/>
        <rect x="208" y="85" width="52" height="175" rx="7" fill={`url(#${p}sg2)`} opacity="0.75"/>
        <path d="M40,205 L82,205 L82,175 L124,175 L124,145 L166,145 L166,115 L208,115 L208,85 L260,85" fill="none" stroke={`url(#${p}edgeG)`} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.5"/>
        <path d="M40,205 L82,205 L82,175 L124,175 L124,145 L166,145 L166,115 L208,115 L208,85 L260,85" fill="none" stroke={`url(#${p}edgeG)`} strokeWidth="6" strokeLinejoin="round" strokeLinecap="round" opacity="0.08" filter={`url(#${p}gl)`}/>
        <path className={`${p}trail-line`} d="M40,205 L82,205 L82,175 L124,175 L124,145 L166,145 L166,115 L208,115 L208,85 L260,85" fill="none" stroke={`url(#${p}edgeG)`} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
        <circle className={`${p}stair-glow-1`} cx="61" cy="205" r="18" fill="#38bdf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-2`} cx="103" cy="175" r="18" fill="#38bdf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-3`} cx="145" cy="145" r="18" fill="#818cf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-4`} cx="187" cy="115" r="18" fill="#818cf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-5`} cx="234" cy="85" r="20" fill="#c084fc" opacity="0" filter={`url(#${p}stepGlow)`}/>
      </g>

      <g className={`${p}star-anim`} style={{transformOrigin:'234px 56px'}}>
        <circle cx="234" cy="56" r="32" fill={`url(#${p}starGlow)`} className={`${p}star-pulse`} style={{transformOrigin:'234px 56px'}}/>
        <g className={`${p}star-rays`} style={{transformOrigin:'234px 56px'}} opacity="0.3">
          <line x1="234" y1="30" x2="234" y2="24" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="234" y1="82" x2="234" y2="88" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="208" y1="56" x2="202" y2="56" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="260" y1="56" x2="266" y2="56" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
        </g>
        <polygon points="234,36 238.5,50 254,50 241.5,60 246,75 234,65 222,75 226.5,60 214,50 229.5,50" fill={`url(#${p}starCore)`} opacity="0.95"/>
        <polygon points="234,42 237,52 247,52 239,58 242,69 234,62 226,69 229,58 221,52 231,52" fill="#fef9c3" opacity="0.9"/>
      </g>

      <g className={`${p}person-container`} filter={`url(#${p}pShadow)`}>
        <g transform="translate(145, 98)">
          <g className={`${p}sit-body`}>
            <ellipse cx="4" cy="47" rx="10" ry="2.5" fill="rgba(0,0,0,0.10)"/>
            <path d="M-4,22 L-8,30 L-2,38 L-6,44" stroke="#444" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
            <path d="M-6,44 L-11,46 L-3,47 L-5,44" fill="#552200" stroke="none"/>
            <path d="M4,22 L8,30 L14,36 L12,43" stroke="#333" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
            <path d="M12,43 L7,45 L15,46 L13,43" fill="#441a00" stroke="none"/>
            <g style={{transform:'rotate(5deg)',transformOrigin:'0px 12px'}}>
              <path d="M-9,4 C-9,0 -5,-3 0,-3 C5,-3 9,0 9,4 L8,23 L-8,23 Z" fill={`url(#${p}shirtG)`}/>
              <path d="M-4,-1 Q0,-4 4,-1" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
              <path d="M-6,4 Q-4,12 -5,20" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            </g>
            <path d="M-7,6 L-10,18 L-6,28" stroke={`url(#${p}skinG)`} strokeWidth="4.2" strokeLinecap="round" fill="none"/>
            <circle cx="-5.5" cy="29" r="2.3" fill="#ff9966"/>
            <path d="M7,6 L11,14 L7,-6" stroke="#e88855" strokeWidth="4.2" strokeLinecap="round" fill="none"/>
            <circle cx="6.5" cy="-7" r="2.3" fill="#e88855"/>
            <rect x="-2.8" y="-8" width="5.6" height="6" rx="2.8" fill={`url(#${p}skinG)`}/>
            <g style={{transform:'rotate(4deg)',transformOrigin:'0px -17px'}}>
              <circle cx="0" cy="-17" r="11" fill={`url(#${p}skinG)`}/>
              <path d="M-11,-22 Q-10,-31 0,-33 Q10,-31 11,-22 L11,-18 Q9,-24 0,-26 Q-9,-24 -11,-18 Z" fill="#1a1a1a"/>
              <path d="M-11,-22 Q-11.5,-18 -10,-15" stroke="#1a1a1a" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
              <path d="M-4,-31 Q0,-32 3,-30" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              <ellipse cx="-10.5" cy="-16" rx="2.2" ry="2.8" fill="#e88855"/>
              <circle cx="4" cy="-18" r="1.8" fill="#1a1a1a"/>
              <circle cx="4.8" cy="-18.8" r="0.7" fill="white"/>
              <path d="M1,-21.5 Q3.5,-23 6.5,-21.5" stroke="#1a1a1a" strokeWidth="1.1" fill="none" strokeLinecap="round"/>
              <path d="M5,-14 Q5.5,-13 5,-12.5" stroke="#dd8866" strokeWidth="0.7" fill="none" strokeLinecap="round"/>
              <ellipse cx="4" cy="-11" rx="2" ry="1.2" fill="#cc7744" opacity="0.45"/>
            </g>
            <g className={`${p}thought-bubble`} style={{transformOrigin:'18px -35px'}}>
              <circle cx="10" cy="-22" r="1.5" fill="rgba(255,255,255,0.4)"/>
              <circle cx="14" cy="-27" r="2" fill="rgba(255,255,255,0.5)"/>
              <ellipse cx="22" cy="-38" rx="12" ry="10" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8"/>
              <g className={`${p}bulb-glow`}>
                <path d="M20,-42 Q18,-38 20,-36 L24,-36 Q26,-38 24,-42 Q22,-44 20,-42 Z" fill="#fde047" opacity="0.9"/>
                <line x1="22" y1="-35" x2="22" y2="-33" stroke="#fde047" strokeWidth="1" strokeLinecap="round" opacity="0.8"/>
                <line x1="20.5" y1="-34" x2="23.5" y2="-34" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" opacity="0.6"/>
                <line x1="22" y1="-47" x2="22" y2="-49" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" opacity="0.5"/>
                <line x1="16" y1="-42" x2="14" y2="-43" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" opacity="0.5"/>
                <line x1="28" y1="-42" x2="30" y2="-43" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" opacity="0.5"/>
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}

// ─── REGRESSION SVG ──────────────────────────────────────────────────────────
function RegressionSVG({ p }: { p: string }) {
  return (
    <svg viewBox="0 0 340 280" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id={`${p}sg1`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(56, 189, 248, 0.25)"/>
          <stop offset="100%" stopColor="rgba(56, 189, 248, 0.03)"/>
        </linearGradient>
        <linearGradient id={`${p}sg2`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(99, 102, 241, 0.25)"/>
          <stop offset="100%" stopColor="rgba(99, 102, 241, 0.03)"/>
        </linearGradient>
        <linearGradient id={`${p}edgeG`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#38bdf8"/>
          <stop offset="50%" stopColor="#818cf8"/>
          <stop offset="100%" stopColor="#c084fc"/>
        </linearGradient>
        <radialGradient id={`${p}starGlow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fde047" stopOpacity="0.8"/>
          <stop offset="60%" stopColor="#fde047" stopOpacity="0.15"/>
          <stop offset="100%" stopColor="#fde047" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id={`${p}starCore`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fef9c3"/>
          <stop offset="100%" stopColor="#fde047"/>
        </radialGradient>
        <linearGradient id={`${p}shirtG`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff6666"/>
          <stop offset="100%" stopColor="#ee4444"/>
        </linearGradient>
        <linearGradient id={`${p}skinG`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffaa77"/>
          <stop offset="100%" stopColor="#ff9966"/>
        </linearGradient>
        <filter id={`${p}gl`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="4" result="b"/>
          <feComposite in="SourceGraphic" in2="b" operator="over"/>
        </filter>
        <filter id={`${p}pShadow`}>
          <feDropShadow dx="1" dy="3" stdDeviation="2.5" floodColor="rgba(0,0,0,0.22)"/>
        </filter>
        <filter id={`${p}stepGlow`} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="8"/>
        </filter>
        <style>{`
          .${p}person-container { animation: ${p}personFade 8s ease forwards; }
          @keyframes ${p}personFade { 0%{opacity:0} 5%{opacity:1} 100%{opacity:1} }
          .${p}w-leg-l { animation: ${p}wLegL 0.5s cubic-bezier(.35,.05,.55,.95) infinite alternate; }
          .${p}w-leg-r { animation: ${p}wLegR 0.5s cubic-bezier(.35,.05,.55,.95) infinite alternate; }
          @keyframes ${p}wLegL { 0%{transform:rotate(-22deg)} 100%{transform:rotate(22deg)} }
          @keyframes ${p}wLegR { 0%{transform:rotate(22deg)} 100%{transform:rotate(-22deg)} }
          .${p}w-arm-l { animation: ${p}wArmL 0.5s cubic-bezier(.35,.05,.55,.95) infinite alternate; }
          .${p}w-arm-r { animation: ${p}wArmR 0.5s cubic-bezier(.35,.05,.55,.95) infinite alternate; }
          @keyframes ${p}wArmL { 0%{transform:rotate(28deg)} 100%{transform:rotate(-22deg)} }
          @keyframes ${p}wArmR { 0%{transform:rotate(-22deg)} 100%{transform:rotate(28deg)} }
          .${p}w-body { animation: ${p}wBounce 0.25s ease-in-out infinite alternate; }
          @keyframes ${p}wBounce { 0%{transform:translateY(0) rotate(0deg)} 100%{transform:translateY(-1.5px) rotate(-1.5deg)} }
          .${p}pose-walk { animation: ${p}pWalk 8s ease forwards; }
          @keyframes ${p}pWalk { 0%{opacity:1} 45%{opacity:1} 52%{opacity:0} 100%{opacity:0} }
          .${p}pose-sit { animation: ${p}pSit 8s ease forwards; }
          @keyframes ${p}pSit { 0%{opacity:0} 45%{opacity:0} 55%{opacity:1} 100%{opacity:1} }
          .${p}sit-body { animation: ${p}sitBreathe 3s ease-in-out infinite; }
          @keyframes ${p}sitBreathe { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-0.8px)} }
          .${p}thought-bubble { animation: ${p}thoughtAppear 8s ease forwards, ${p}thoughtLoop 4s ease-in-out 5s infinite; }
          @keyframes ${p}thoughtAppear { 0%{opacity:0;transform:scale(0)} 58%{opacity:0;transform:scale(0)} 68%{opacity:1;transform:scale(1.1)} 72%{opacity:1;transform:scale(1)} 100%{opacity:1;transform:scale(1)} }
          @keyframes ${p}thoughtLoop { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-2px) scale(1.05)} }
          .${p}bulb-glow { animation: ${p}bulbPulse 2.5s ease-in-out 5s infinite; opacity: 0.7; }
          @keyframes ${p}bulbPulse { 0%,100%{opacity:0.7} 50%{opacity:1} }
          .${p}star-anim { opacity: 1; }
          .${p}star-pulse { animation: ${p}starPulse 2s ease-in-out infinite; }
          @keyframes ${p}starPulse { 0%,100%{opacity:0.6;transform:scale(1)} 50%{opacity:1;transform:scale(1.15)} }
          .${p}star-rays { animation: ${p}starRays 3s linear infinite; }
          @keyframes ${p}starRays { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
          .${p}stair-glow-4,.${p}stair-glow-5 { opacity: 0; }
          .${p}stair-glow-3 { animation: ${p}sGlow3 8s ease forwards; }
          @keyframes ${p}sGlow3 { 0%{opacity:0.5} 15%{opacity:0.6} 30%{opacity:0.1} 100%{opacity:0} }
          .${p}stair-glow-2 { animation: ${p}sGlow2 8s ease forwards; }
          @keyframes ${p}sGlow2 { 0%{opacity:0} 15%{opacity:0.1} 25%{opacity:0.6} 45%{opacity:0.1} 100%{opacity:0} }
          .${p}stair-glow-1 { animation: ${p}sGlow1 8s ease forwards, ${p}sGlow1loop 3s ease-in-out 8s infinite; }
          @keyframes ${p}sGlow1 { 0%{opacity:0} 42%{opacity:0} 52%{opacity:0.55} 100%{opacity:0.35} }
          @keyframes ${p}sGlow1loop { 0%,100%{opacity:0.25} 50%{opacity:0.55} }
          .${p}ripple-4,.${p}ripple-5 { opacity: 0; }
          .${p}ripple-3 { animation: ${p}rip3 8s ease forwards; }
          @keyframes ${p}rip3 { 0%{r:0;opacity:0} 5%{r:8;opacity:0.5} 12%{r:16;opacity:0} 100%{opacity:0} }
          .${p}ripple-2 { animation: ${p}rip2 8s ease forwards; }
          @keyframes ${p}rip2 { 0%,22%{r:0;opacity:0} 27%{r:8;opacity:0.5} 35%{r:16;opacity:0} 100%{opacity:0} }
          .${p}ripple { animation: ${p}rip1 8s ease forwards; }
          @keyframes ${p}rip1 { 0%,45%{r:0;opacity:0} 50%{r:8;opacity:0.5} 58%{r:16;opacity:0} 100%{opacity:0} }
          .${p}trail-line { opacity: 0; }
          .${p}amb { animation: ${p}ambFloat 6s infinite ease-in-out; }
          .${p}amb:nth-child(2){animation-delay:1.2s;animation-duration:7s}.${p}amb:nth-child(3){animation-delay:2.8s;animation-duration:5s}
          .${p}amb:nth-child(4){animation-delay:0.6s;animation-duration:8s}.${p}amb:nth-child(5){animation-delay:1.8s;animation-duration:6.5s}
          .${p}amb:nth-child(6){animation-delay:3.5s;animation-duration:5.5s}.${p}amb:nth-child(7){animation-delay:0.3s;animation-duration:9s}
          @keyframes ${p}ambFloat{0%{transform:translateY(0) scale(1);opacity:0}20%{opacity:0.5}50%{transform:translateY(-18px) scale(1.3);opacity:0.35}80%{opacity:0.15}100%{transform:translateY(-35px) scale(0.8);opacity:0}}
          .${p}geo{animation:${p}geoFloat 8s ease-in-out infinite}
          @keyframes ${p}geoFloat{0%,100%{transform:translateY(0) rotate(0deg);opacity:0.12}50%{transform:translateY(-8px) rotate(180deg);opacity:0.25}}
        `}</style>
      </defs>

      <circle className={`${p}amb`} cx="55" cy="190" r="1.8" fill="#38bdf8"/>
      <circle className={`${p}amb`} cx="170" cy="120" r="2" fill="#fde047"/>
      <circle className={`${p}amb`} cx="35" cy="90" r="1.3" fill="#a78bfa"/>
      <circle className={`${p}amb`} cx="285" cy="155" r="1.8" fill="#34d399"/>
      <circle className={`${p}amb`} cx="305" cy="98" r="1.2" fill="#f472b6"/>
      <circle className={`${p}amb`} cx="130" cy="70" r="1.5" fill="#38bdf8"/>
      <circle className={`${p}amb`} cx="260" cy="200" r="1" fill="#818cf8"/>
      <rect className={`${p}geo`} x="20" y="120" width="5" height="5" rx="1" fill="#38bdf8" opacity="0.15" style={{transformOrigin:'22px 122px'}}/>
      <polygon className={`${p}geo`} points="310,130 313,124 316,130" fill="#c084fc" opacity="0.12" style={{transformOrigin:'313px 127px',animationDelay:'2s'}}/>
      <circle className={`${p}geo`} cx="290" cy="60" r="2.5" fill="#fde047" opacity="0.1" style={{animationDelay:'4s'}}/>

      <g>
        <rect x="40" y="205" width="42" height="55" rx="7" fill={`url(#${p}sg1)`} opacity="0.65"/>
        <rect x="82" y="175" width="42" height="85" rx="7" fill={`url(#${p}sg1)`} opacity="0.70"/>
        <rect x="124" y="145" width="42" height="115" rx="7" fill={`url(#${p}sg2)`} opacity="0.65"/>
        <rect x="166" y="115" width="42" height="145" rx="7" fill={`url(#${p}sg2)`} opacity="0.70"/>
        <rect x="208" y="85" width="52" height="175" rx="7" fill={`url(#${p}sg2)`} opacity="0.75"/>
        <path d="M40,205 L82,205 L82,175 L124,175 L124,145 L166,145 L166,115 L208,115 L208,85 L260,85" fill="none" stroke={`url(#${p}edgeG)`} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.5"/>
        <path d="M40,205 L82,205 L82,175 L124,175 L124,145 L166,145 L166,115 L208,115 L208,85 L260,85" fill="none" stroke={`url(#${p}edgeG)`} strokeWidth="6" strokeLinejoin="round" strokeLinecap="round" opacity="0.08" filter={`url(#${p}gl)`}/>
        <path className={`${p}trail-line`} d="M40,205 L82,205 L82,175 L124,175 L124,145 L166,145 L166,115 L208,115 L208,85 L260,85" fill="none" stroke={`url(#${p}edgeG)`} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
        <circle className={`${p}stair-glow-1`} cx="61" cy="205" r="18" fill="#38bdf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-2`} cx="103" cy="175" r="18" fill="#38bdf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-3`} cx="145" cy="145" r="18" fill="#818cf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-4`} cx="187" cy="115" r="18" fill="#818cf8" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}stair-glow-5`} cx="234" cy="85" r="20" fill="#c084fc" opacity="0" filter={`url(#${p}stepGlow)`}/>
        <circle className={`${p}ripple`} cx="61" cy="205" r="0" fill="none" stroke="#38bdf8" strokeWidth="1"/>
        <circle className={`${p}ripple-2`} cx="103" cy="175" r="0" fill="none" stroke="#38bdf8" strokeWidth="1"/>
        <circle className={`${p}ripple-3`} cx="145" cy="145" r="0" fill="none" stroke="#818cf8" strokeWidth="1"/>
        <circle className={`${p}ripple-4`} cx="187" cy="115" r="0" fill="none" stroke="#818cf8" strokeWidth="1"/>
        <circle className={`${p}ripple-5`} cx="234" cy="85" r="0" fill="none" stroke="#c084fc" strokeWidth="1"/>
      </g>

      <g className={`${p}star-anim`} style={{transformOrigin:'234px 56px'}}>
        <circle cx="234" cy="56" r="32" fill={`url(#${p}starGlow)`} className={`${p}star-pulse`} style={{transformOrigin:'234px 56px'}}/>
        <g className={`${p}star-rays`} style={{transformOrigin:'234px 56px'}} opacity="0.3">
          <line x1="234" y1="30" x2="234" y2="24" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="234" y1="82" x2="234" y2="88" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="208" y1="56" x2="202" y2="56" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="260" y1="56" x2="266" y2="56" stroke="#fde047" strokeWidth="1.5" strokeLinecap="round"/>
        </g>
        <polygon points="234,36 238.5,50 254,50 241.5,60 246,75 234,65 222,75 226.5,60 214,50 229.5,50" fill={`url(#${p}starCore)`} opacity="0.95"/>
        <polygon points="234,42 237,52 247,52 239,58 242,69 234,62 226,69 229,58 221,52 231,52" fill="#fef9c3" opacity="0.9"/>
      </g>

      <g className={`${p}person-container`} filter={`url(#${p}pShadow)`}>
        <g transform="translate(61, 158)">
          <g>
            <animateTransform attributeName="transform" type="translate"
              values="84,-60; 84,-60; 42,-30; 42,-30; 0,0; 0,0"
              keyTimes="0; 0.05; 0.25; 0.30; 0.48; 1"
              keySplines="0.25 0.1 0.25 1; 0.42 0 0.58 1; 0.25 0.1 0.25 1; 0.42 0 0.58 1; 0.25 0.1 0.25 1"
              calcMode="spline" dur="8s" fill="freeze"/>

            <g className={`${p}pose-walk`}>
              <g className={`${p}w-body`} style={{transform:'scaleX(-1)',transformOrigin:'0px 0px'}}>
                <ellipse cx="0" cy="47" rx="13" ry="3" fill="rgba(0,0,0,0.15)">
                  <animate attributeName="rx" values="13;11;13" dur="0.5s" repeatCount="indefinite"/>
                </ellipse>
                <g className={`${p}w-leg-l`} style={{transformOrigin:'-1px 22px'}}>
                  <path d="M-1,22 L-6,34 L-7,41" stroke="#444" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
                  <path d="M-7,41 L-12,43.5 L-4,44.5 L-6,41" fill="#552200" stroke="none"/>
                </g>
                <g className={`${p}w-leg-r`} style={{transformOrigin:'2px 22px'}}>
                  <path d="M2,22 L8,34 L9,41" stroke="#333" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
                  <path d="M9,41 L4,43.5 L12,44.5 L10,41" fill="#441a00" stroke="none"/>
                </g>
                <path d="M-9,4 C-9,0 -5,-3 0,-3 C5,-3 9,0 9,4 L8,23 L-8,23 Z" fill={`url(#${p}shirtG)`}/>
                <path d="M-4,-1 Q0,-4 4,-1" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
                <path d="M-6,4 Q-4,12 -5,20" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <g className={`${p}w-arm-l`} style={{transformOrigin:'-7px 2px'}}>
                  <path d="M-7,2 L-14,13 L-16,18" stroke={`url(#${p}skinG)`} strokeWidth="4.2" strokeLinecap="round" fill="none"/>
                  <circle cx="-16.5" cy="19" r="2.3" fill="#ff9966"/>
                </g>
                <g className={`${p}w-arm-r`} style={{transformOrigin:'7px 2px'}}>
                  <path d="M7,2 L14,13 L16,18" stroke="#e88855" strokeWidth="4.2" strokeLinecap="round" fill="none"/>
                  <circle cx="16.5" cy="19" r="2.3" fill="#e88855"/>
                </g>
                <rect x="-2.8" y="-8" width="5.6" height="6" rx="2.8" fill={`url(#${p}skinG)`}/>
                <circle cx="0" cy="-16" r="11" fill={`url(#${p}skinG)`}/>
                <path d="M-11,-21 Q-10,-30 0,-32 Q10,-30 11,-21 L11,-17 Q9,-23 0,-25 Q-9,-23 -11,-17 Z" fill="#1a1a1a"/>
                <path d="M-11,-21 Q-11.5,-17 -10,-14" stroke="#1a1a1a" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
                <path d="M-4,-30 Q0,-31 3,-29" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <ellipse cx="-10.5" cy="-14.5" rx="2.2" ry="2.8" fill="#e88855"/>
                <circle cx="3.5" cy="-16" r="1.8" fill="#1a1a1a"/>
                <circle cx="4.2" cy="-16.5" r="0.7" fill="white"/>
                <path d="M1.5,-19.5 Q3.5,-19 6.5,-20.5" stroke="#1a1a1a" strokeWidth="1.1" fill="none" strokeLinecap="round"/>
                <path d="M5,-14 Q5.5,-13 5,-12.5" stroke="#dd8866" strokeWidth="0.7" fill="none" strokeLinecap="round"/>
                <path d="M2,-10.5 Q4,-12 6.5,-10.5" stroke="#cc7744" strokeWidth="0.9" fill="none" strokeLinecap="round"/>
              </g>
            </g>

            <g className={`${p}pose-sit`}>
              <g className={`${p}sit-body`}>
                <ellipse cx="4" cy="47" rx="10" ry="2.5" fill="rgba(0,0,0,0.10)"/>
                <path d="M-4,22 L-8,30 L-2,38 L-6,44" stroke="#444" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
                <path d="M-6,44 L-11,46 L-3,47 L-5,44" fill="#552200" stroke="none"/>
                <path d="M4,22 L8,30 L14,36 L12,43" stroke="#333" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
                <path d="M12,43 L7,45 L15,46 L13,43" fill="#441a00" stroke="none"/>
                <g style={{transform:'rotate(5deg)',transformOrigin:'0px 12px'}}>
                  <path d="M-9,4 C-9,0 -5,-3 0,-3 C5,-3 9,0 9,4 L8,23 L-8,23 Z" fill={`url(#${p}shirtG)`}/>
                  <path d="M-4,-1 Q0,-4 4,-1" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
                  <path d="M-6,4 Q-4,12 -5,20" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                </g>
                <path d="M-7,6 L-10,18 L-6,28" stroke={`url(#${p}skinG)`} strokeWidth="4.2" strokeLinecap="round" fill="none"/>
                <circle cx="-5.5" cy="29" r="2.3" fill="#ff9966"/>
                <path d="M7,6 L11,14 L7,-6" stroke="#e88855" strokeWidth="4.2" strokeLinecap="round" fill="none"/>
                <circle cx="6.5" cy="-7" r="2.3" fill="#e88855"/>
                <rect x="-2.8" y="-8" width="5.6" height="6" rx="2.8" fill={`url(#${p}skinG)`}/>
                <g style={{transform:'rotate(4deg)',transformOrigin:'0px -17px'}}>
                  <circle cx="0" cy="-17" r="11" fill={`url(#${p}skinG)`}/>
                  <path d="M-11,-22 Q-10,-31 0,-33 Q10,-31 11,-22 L11,-18 Q9,-24 0,-26 Q-9,-24 -11,-18 Z" fill="#1a1a1a"/>
                  <path d="M-11,-22 Q-11.5,-18 -10,-15" stroke="#1a1a1a" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
                  <path d="M-4,-31 Q0,-32 3,-30" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                  <ellipse cx="-10.5" cy="-16" rx="2.2" ry="2.8" fill="#e88855"/>
                  <circle cx="4" cy="-18" r="1.8" fill="#1a1a1a"/>
                  <circle cx="4.8" cy="-18.8" r="0.7" fill="white"/>
                  <path d="M1,-21.5 Q3.5,-23 6.5,-21.5" stroke="#1a1a1a" strokeWidth="1.1" fill="none" strokeLinecap="round"/>
                  <path d="M5,-14 Q5.5,-13 5,-12.5" stroke="#dd8866" strokeWidth="0.7" fill="none" strokeLinecap="round"/>
                  <ellipse cx="4" cy="-11" rx="2" ry="1.2" fill="#cc7744" opacity="0.45"/>
                </g>
                <g className={`${p}thought-bubble`} style={{transformOrigin:'18px -35px'}}>
                  <circle cx="10" cy="-22" r="1.5" fill="rgba(255,255,255,0.4)"/>
                  <circle cx="14" cy="-27" r="2" fill="rgba(255,255,255,0.5)"/>
                  <ellipse cx="22" cy="-38" rx="12" ry="10" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8"/>
                  <g className={`${p}bulb-glow`}>
                    <path d="M20,-42 Q18,-38 20,-36 L24,-36 Q26,-38 24,-42 Q22,-44 20,-42 Z" fill="#fde047" opacity="0.9"/>
                    <line x1="22" y1="-35" x2="22" y2="-33" stroke="#fde047" strokeWidth="1" strokeLinecap="round" opacity="0.8"/>
                    <line x1="20.5" y1="-34" x2="23.5" y2="-34" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" opacity="0.6"/>
                    <line x1="22" y1="-47" x2="22" y2="-49" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" opacity="0.5"/>
                    <line x1="16" y1="-42" x2="14" y2="-43" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" opacity="0.5"/>
                    <line x1="28" y1="-42" x2="30" y2="-43" stroke="#fde047" strokeWidth="0.8" strokeLinecap="round" opacity="0.5"/>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
interface MotivationBannerProps {
  goals: Goal[];
}

const QUOTE_KEYS = [
  'quote1',
  'quote2',
  'quote3',
  'quote4',
  'quote5',
  'quote6',
  'quote7',
  'quote8',
  'quote9',
  'quote10',
] as const;

const MotivationBanner: React.FC<MotivationBannerProps> = ({ goals }) => {
  const { t, language } = useTranslation();
  const uid = useId().replace(/:/g, '');
  const p = `mb${uid}`;

  const state = useMemo(() => getMotivationState(goals), [goals]);
  const quoteKey = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * QUOTE_KEYS.length);
    return QUOTE_KEYS[randomIndex];
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps -- pick a new quote when the language changes

  if (goals.length === 0) return null;

  return (
    <div className={`motivation-banner motivation-banner--${state}`}>
      <div className="motivation-banner__illustration">
        {state === 'progress' && <ProgressSVG p={p} />}
        {state === 'static' && <StaticSVG p={p} />}
        {state === 'regression' && <RegressionSVG p={p} />}
      </div>
      <div className="motivation-banner__content">
        <h2 className="motivation-banner__quote">
          &ldquo;{t(`motivationBanner.quotes.${quoteKey}`)}&rdquo;
        </h2>
        <p className="motivation-banner__subtext">
          {t(`motivationBanner.${state}.subtext`)}
        </p>
      </div>
    </div>
  );
};

export default MotivationBanner;
